import {
  isNotNull,
  OnboardingStepType,
  OrgAdminFragment,
  useOrgAdminsQuery,
} from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Container,
  DialogActions,
  FullDialog,
  Link,
  RadioButton,
  RadioGroup,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { orgRootPath } from '../organization/useOrg'
import { useOrgId } from '../useOrgId'
import { useAssignUserToStep } from '../utils/useAssignUserToStep'

const adminSort = (a: OrgAdminFragment, b: OrgAdminFragment) =>
  a.lastName?.localeCompare(b.lastName || '') || a.firstName?.localeCompare(b.firstName || '') || 0

type AdminSelectorProps = {
  selectedAdmin: OrgAdminFragment | undefined
  admins: Array<OrgAdminFragment>
  onChange: (admin: OrgAdminFragment | null) => void
}

const AdminSelector = ({ selectedAdmin, admins, onChange }: AdminSelectorProps) => {
  const [currentSelection, setCurrentSelection] = React.useState<string>(selectedAdmin?.id.toString() || '')

  return (
    <RadioGroup
      value={currentSelection}
      onChange={(_, value) => {
        setCurrentSelection(value)
        const admin = admins.find((a) => a.id.toString() === value)
        onChange(admin || null)
      }}
    >
      {admins.map((admin) => (
        <RadioButton
          key={admin.id}
          value={admin.id.toString()}
          label={
            <Typography
              gutterBottom
              variant="body2"
              color={currentSelection === admin.id.toString() ? 'primary' : 'inherit'}
              fontWeight={currentSelection === admin.id.toString() ? 'bold' : 'normal'}
            >
              {`${admin.firstName} ${admin.lastName}`}
              {admin.role?.label && (
                <Typography variant="caption" color="textSecondary">
                  {` - ${admin.role.label.split(',').join(', ')}`}
                </Typography>
              )}
              <br />
              <Typography variant="caption" color="textSecondary" fontSize="0.9rem">
                {admin.email}
              </Typography>
            </Typography>
          }
        />
      ))}
    </RadioGroup>
  )
}

type HealthcheckAssignmentDialogProps = {
  open: boolean
  onClose: () => void
  stepType: OnboardingStepType
  healthcheckTitle: string
  currentAssignee?: {
    id: number
    name: string
    email?: string | null
  } | null
}

export const HealthcheckAssignmentDialog = ({
  open,
  onClose,
  stepType,
  healthcheckTitle,
  currentAssignee,
}: HealthcheckAssignmentDialogProps) => {
  const orgId = useOrgId()
  const orgPath = orgRootPath(orgId)
  const { data: orgAdminsData } = useOrgAdminsQuery({ orgId })
  const admins = orgAdminsData?.usersWithRole?.filter(isNotNull).sort(adminSort) || []

  const currentAssignedAdmin = currentAssignee
    ? admins.find((admin) => admin.id === currentAssignee.id)
    : undefined

  const [selectedAdmin, setSelectedAdmin] = React.useState<OrgAdminFragment | null>(
    currentAssignedAdmin || null
  )
  const [isAssigning, setIsAssigning] = React.useState(false)

  const { mutate: assignUserToStep } = useAssignUserToStep({
    onSuccess: () => {
      setIsAssigning(false)
      onClose()
    },
    onError: () => {
      setIsAssigning(false)
    },
  })

  const handleAssign = () => {
    if (!selectedAdmin) {
      console.error('No admin selected for assignment')
      return
    }

    setIsAssigning(true)
    assignUserToStep({
      orgId,
      userId: selectedAdmin.id,
      stepType,
    })
  }

  const hasChanges = selectedAdmin?.id !== currentAssignedAdmin?.id

  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={<Typography variant="h2">Assign task: {healthcheckTitle}</Typography>}
    >
      <Container>
        <Typography variant="body2" gutterBottom>
          Select the person who should be responsible for this step. We will send them instructions and a
          sign-in link to complete it.
        </Typography>

        <Box mb={3}>
          <AdminSelector selectedAdmin={currentAssignedAdmin} admins={admins} onChange={setSelectedAdmin} />
        </Box>

        <Typography variant="body2" gutterBottom>
          <strong>Assignee not listed?</strong> Assign them a role in the{' '}
          <Link to={`${orgPath}/company-profile`}>Company roles</Link> page.
        </Typography>

        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={onClose} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAssign}
            disabled={!hasChanges || !selectedAdmin || isAssigning}
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Container>
    </FullDialog>
  )
}
